import React, { useState, useEffect } from 'react';
import VisibilityBadges from './VisibilityBadges';

const TITLES = {
  menu: 'Menu Details',
  'menu-group': 'Menu Group Details',
  product: 'Product Details',
  'modifier-group': 'Modifier Group Details',
  modifier: 'Modifier Details'
};

export default function TableDetailPanel({ item, onClose }) {
  const { type, row } = item;

  return (
    <div className="inline-detail-panel">
      <div className="inline-detail-header">
        <h3>{TITLES[type] || 'Details'}</h3>
        <button className="close-btn" onClick={onClose}>✕</button>
      </div>
      <div className="inline-detail-body">
        {type === 'product' ? (
          <ProductDetailBody guid={row.guid} fallback={row} />
        ) : (
          <SimpleDetailBody type={type} row={row} />
        )}
      </div>
    </div>
  );
}

function SimpleDetailBody({ type, row }) {
  if (type === 'menu') {
    return (
      <div className="info-grid">
        <InfoItem label="Name" value={row.name} />
        <InfoItem label="Description" value={row.description || 'N/A'} />
        <InfoItem label="Groups" value={row.group_count} />
        <InfoItem label="Items" value={row.item_count} />
        <InfoItem label="Synced" value={new Date(row.synced_at).toLocaleString()} />
        <InfoItem label="GUID" value={<code>{row.guid}</code>} />
      </div>
    );
  }

  if (type === 'menu-group') {
    return (
      <div className="info-grid">
        <InfoItem label="Name" value={row.name} />
        <InfoItem label="Menu" value={row.menu_name || 'N/A'} />
        <InfoItem label="Parent Group" value={row.parent_group_name || 'Root'} />
        <InfoItem label="Items" value={row.item_count} />
        <InfoItem label="Synced" value={new Date(row.synced_at).toLocaleString()} />
        <InfoItem label="GUID" value={<code>{row.guid}</code>} />
      </div>
    );
  }

  if (type === 'modifier-group') {
    return (
      <div className="info-grid">
        <InfoItem label="Name" value={row.name} />
        <InfoItem label="Selection Range" value={`${row.min_selected} - ${row.max_selected}`} />
        <InfoItem label="Modifiers" value={row.modifier_count} />
        <InfoItem label="Used By Items" value={row.item_count} />
        <InfoItem label="Synced" value={new Date(row.synced_at).toLocaleString()} />
        <InfoItem label="GUID" value={<code>{row.guid}</code>} />
      </div>
    );
  }

  if (type === 'modifier') {
    return (
      <div className="info-grid">
        <InfoItem label="Name" value={row.name} />
        <InfoItem label="Modifier Group" value={row.modifier_group_name || 'N/A'} />
        <InfoItem
          label="Price Adjustment"
          value={row.price_adjustment ? `+$${row.price_adjustment.toFixed(2)}` : 'No charge'}
        />
        <InfoItem label="Sort Order" value={row.sort_order ?? 'N/A'} />
        <InfoItem label="Synced" value={new Date(row.synced_at).toLocaleString()} />
        <InfoItem label="GUID" value={<code>{row.guid}</code>} />
      </div>
    );
  }

  return null;
}

function ProductDetailBody({ guid, fallback }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setLoading(true);
        const response = await fetch(`/api/products/${guid}`);
        if (!response.ok) throw new Error('Failed to fetch product details');
        const data = await response.json();
        if (!cancelled) setDetails(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [guid]);

  if (loading) return <p className="loading">Loading...</p>;
  if (error) return <div className="error">✕ {error}</div>;
  if (!details) return null;

  const { product, location, taxes, allergens, images, modifierGroups } = details;

  return (
    <div>
      <h2>{product.name}</h2>
      {location && <span className="location-cell" title={location.guid}>{location.name}</span>}
      {product.pos_name && <p className="pos-name">POS Name: {product.pos_name}</p>}

      {images && images.length > 0 && (
        <div className="image-section">
          <img
            src={images[0].image_url}
            alt={product.name}
            className="detail-image"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        </div>
      )}

      <div className="details-section">
        <h3>💰 Pricing</h3>
        <div className="info-grid">
          <InfoItem label="Price" value={<span className="price">${(product.price || 0).toFixed(2)}</span>} />
          <InfoItem label="Strategy" value={product.pricing_strategy || 'N/A'} />
          <InfoItem label="Category" value={product.sales_category_name || 'N/A'} />
        </div>
      </div>

      <div className="details-section">
        <h3>👁️ Visibility</h3>
        <VisibilityBadges visibility={product.visibility} />
      </div>

      <div className="details-section">
        <h3>🕒 History</h3>
        <div className="info-grid">
          {product.created_at && (
            <InfoItem label="Created" value={new Date(product.created_at).toLocaleString()} />
          )}
          {product.synced_at && (
            <InfoItem label="Updated" value={new Date(product.synced_at).toLocaleString()} />
          )}
        </div>
      </div>

      {taxes && taxes.length > 0 && (
        <div className="details-section">
          <h3>🧾 Taxes</h3>
          <div className="taxes-list">
            {taxes.map((tax, idx) => (
              <div key={idx} className="tax-item">
                <span className="tax-name">{tax.tax_name || 'Unknown Tax'}</span>
                {tax.tax_percentage != null && <span className="tax-rate">{tax.tax_percentage}%</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {allergens && allergens.length > 0 && (
        <div className="details-section">
          <h3>⚠️ Allergens</h3>
          <div className="allergens-list">
            {allergens.map((a, idx) => <span key={idx} className="allergen-badge">{a}</span>)}
          </div>
        </div>
      )}

      {modifierGroups && modifierGroups.length > 0 && (
        <div className="details-section">
          <h3>🔧 Modifiers & Options</h3>
          {modifierGroups.map((group, idx) => (
            <div key={idx} className="modifier-group">
              <h4>{group.name} <span className="modifier-range">(Select {group.min_selected}-{group.max_selected})</span></h4>
              <ul className="modifiers-list">
                {group.modifiers && group.modifiers.map((mod, midx) => (
                  <li key={midx}>
                    <span>{mod.name}</span>
                    {mod.price_adjustment ? <span className="mod-price">+${mod.price_adjustment.toFixed(2)}</span> : null}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function InfoItem({ label, value }) {
  return (
    <div className="info-item">
      <label>{label}</label>
      <span>{value}</span>
    </div>
  );
}
